// ============================================================================
// FSA — MULTI-COLUMN NOTE: COLUMN TOTALS (single derivation)
// ============================================================================
// The note-wide Total row on a multi-column Note (PPE, Intangibles, Owners'
// Capital) is Σ of every class row's column_values, per amount column, with
// the __rounded companions summed alongside.
//
// This existed in THREE places with subtly different rules — the footer in
// MultiColumnClassTable, the section-layout effect in MainNotePane, and the
// save-time recompute in FsaNoteModal.normalizeWorkspaceForSave. They were
// allowed to disagree, and that caused a permanently-dirty Note:
//
//   1. normalizeWorkspaceForSave rewrites class_data.column_values through
//      recomputeFormulaColumns for EVERY multi-column note, but only
//      recomputed column_totals for section-layout ones.
//   2. So PPE persisted normalised column_values next to PRE-normalise
//      column_totals.
//   3. On reload the footer re-derived totals from the (normalised)
//      class_data, they differed from the saved ones, setColumnTotals wrote
//      — after the hydrate snapshot was taken — and isDirty went true.
//   4. Saving repeated the skew, so isDirty never settled: the Save button
//      stayed lit forever and Preview (gated on isDirty) was unreachable.
//
// One function, one rule, used by all three. Keep it that way: a second
// copy is what broke it.
//
// Rules (deliberately identical everywhere):
//   • amount columns only — text / label columns carry no total.
//   • hide_in_print classes are excluded. The PDF skips them, so the
//     persisted Total must match the PDF's presentation.
//   • classes with no column_values contribute nothing. This is what
//     naturally excludes template sub-heading rows — they're grouping
//     labels and hold no values.
//   • both `<code>` and `<code>__rounded` are summed when numeric, so the
//     dict serves the actual view, the rounded view and the PDF verbatim.

import { ROUNDED_SUFFIX } from './rounding';

/** Minimal column shape — satisfied by FormatColumnSpec and the pane's ColumnSpec. */
export interface ColumnTotalsColumn {
  code: string;
  data_type?: string;
}

/** Minimal class shape — satisfied by NoteClassData. */
export interface ColumnTotalsClass {
  column_values?: Record<string, unknown> | null;
  hide_in_print?: boolean | null;
}

/**
 * Σ every amount column (and its __rounded companion) across the note's
 * class rows. Returns a flat dict ready to persist to
 * workspace.column_totals and to render in the footer.
 */
export function sumColumnTotals(
  classData: Record<string, ColumnTotalsClass> | null | undefined,
  columns: ReadonlyArray<ColumnTotalsColumn> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!classData || !columns || columns.length === 0) return out;

  for (const cd of Object.values(classData)) {
    if (!cd || cd.hide_in_print) continue;
    const cv = cd.column_values;
    if (!cv) continue;

    for (const col of columns) {
      if (col.data_type !== 'amount') continue;

      const actual = cv[col.code];
      if (typeof actual === 'number') {
        out[col.code] = (out[col.code] ?? 0) + actual;
      }

      const roundedKey = col.code + ROUNDED_SUFFIX;
      const rounded = cv[roundedKey];
      if (typeof rounded === 'number') {
        out[roundedKey] = (out[roundedKey] ?? 0) + rounded;
      }
    }
  }

  return out;
}
